// Helpers for turning a drag-and-drop (or <input webkitdirectory>) payload
// into a flat list of files tagged with the folder they came from.
//
// Dropping a folder onto a page gives `dataTransfer.files` a zero-byte
// pseudo-File for the directory. Chrome cannot read it as a request body, so
// uploading it fails with "Failed to fetch" before the request leaves the
// browser. The only way to see inside a dropped folder is the
// webkitGetAsEntry() tree, which this module walks.
//
// Shape returned by every collector:
//   {
//     files:  [{ file: File, relativeDir: 'Sub/Dir' | '' }],
//     dirs:   ['Top', 'Top/Sub', ...]   // every directory encountered
//     failed: [{ name: 'Top/bad.mov', error: 'message' }]
//   }
// relativeDir is '/'-joined and relative to the drop target, never absolute.

// Finder and Explorer litter folders with these. They are never wanted on the NAS.
const JUNK_FILES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

export function isJunkFile(name) {
  return JUNK_FILES.has(name) || String(name).startsWith('._');
}

// Join two '/'-separated path fragments, tolerating empty parts and stray slashes.
export function joinPath(base, rel) {
  const a = String(base || '').replace(/\/+$/, '');
  const b = String(rel || '').replace(/^\/+/, '');
  if (!a) return b;
  if (!b) return a;
  return `${a}/${b}`;
}

function errorMessage(err) {
  if (!err) return 'Could not read file';
  if (typeof err === 'string') return err;
  return err.message || err.name || 'Could not read file';
}

// readEntries() hands back batches (Chrome caps each at 100) and signals the
// end with an empty batch, so it has to be called until it returns nothing.
function readAllEntries(reader) {
  return new Promise((resolve, reject) => {
    const all = [];
    const step = () => reader.readEntries((batch) => {
      if (!batch || batch.length === 0) return resolve(all);
      all.push(...batch);
      step();
    }, reject);
    step();
  });
}

function fileFromEntry(entry) {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

// Walk FileSystemEntry trees obtained from DataTransferItem.webkitGetAsEntry().
export async function collectFromEntries(entries) {
  const files = [];
  const dirs = [];
  const failed = [];

  async function walk(entry, relativeDir) {
    if (entry.isFile) {
      if (isJunkFile(entry.name)) return;
      try {
        const file = await fileFromEntry(entry);
        files.push({ file, relativeDir });
      } catch (err) {
        failed.push({ name: joinPath(relativeDir, entry.name), error: errorMessage(err) });
      }
      return;
    }
    if (entry.isDirectory) {
      const dir = joinPath(relativeDir, entry.name);
      dirs.push(dir);
      let children;
      try {
        children = await readAllEntries(entry.createReader());
      } catch (err) {
        failed.push({ name: `${dir}/`, error: errorMessage(err) });
        return;
      }
      for (const child of children) await walk(child, dir);
    }
  }

  for (const entry of entries) await walk(entry, '');
  return { files, dirs, failed };
}

// FileList from <input type="file"> or <input webkitdirectory>. The directory
// picker reports each file's path inside the chosen folder via
// webkitRelativePath; a plain picker leaves it empty.
export function fromFileList(fileList) {
  const files = [];
  const dirs = new Set();
  for (const file of Array.from(fileList || [])) {
    if (isJunkFile(file.name)) continue;
    const rel = file.webkitRelativePath || '';
    const cut = rel.lastIndexOf('/');
    const relativeDir = cut > 0 ? rel.slice(0, cut) : '';
    if (relativeDir) {
      // Record every ancestor so the caller sees the same dir list a drop would give.
      const parts = relativeDir.split('/');
      for (let i = 1; i <= parts.length; i++) dirs.add(parts.slice(0, i).join('/'));
    }
    files.push({ file, relativeDir });
  }
  return { files, dirs: Array.from(dirs), failed: [] };
}

// Must be called synchronously inside the drop handler: the browser empties
// DataTransferItemList as soon as the event finishes dispatching, so the
// entries have to be grabbed before the first await. The walk itself is async.
export function collectFromDataTransfer(dataTransfer) {
  const items = dataTransfer && dataTransfer.items;
  if (items && items.length) {
    const entries = [];
    let supported = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind !== 'file' || typeof item.webkitGetAsEntry !== 'function') continue;
      supported = true;
      const entry = item.webkitGetAsEntry();
      if (entry) entries.push(entry);
    }
    if (supported) return collectFromEntries(entries);
  }
  return Promise.resolve(fromFileList(dataTransfer ? dataTransfer.files : []));
}

// Directories that no file will create as a side effect. Uploading a file to
// "A/B/c.mov" makes A and A/B on the server, but an empty folder has nothing
// to carry it, so it needs an explicit mkdir. Ancestors of another empty
// directory are dropped because a recursive mkdir of the deeper one creates them.
export function emptyDirsToCreate(files, dirs) {
  const hasFileBelow = (dir) => files.some(
    (f) => f.relativeDir === dir || f.relativeDir.startsWith(`${dir}/`),
  );
  const empty = (dirs || []).filter((d) => d && !hasFileBelow(d));
  return empty.filter((d) => !empty.some((other) => other !== d && other.startsWith(`${d}/`)));
}

// Run thunks with at most `limit` in flight. Never rejects: each slot in the
// result is { ok, value } or { ok, error }, in input order.
export async function runWithConcurrency(tasks, limit) {
  const results = new Array(tasks.length);
  let next = 0;
  async function worker() {
    while (next < tasks.length) {
      const i = next++;
      try {
        results[i] = { ok: true, value: await tasks[i]() };
      } catch (error) {
        results[i] = { ok: false, error };
      }
    }
  }
  const n = Math.max(1, Math.min(limit || 1, tasks.length));
  await Promise.all(Array.from({ length: n }, worker));
  return results;
}
